import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  ListItemButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { BookOpen, Check, ChevronDown, Copy, MailCheck } from 'lucide-react';

import { Doc_Template_Type_Enum_Enum } from '@graphql/__generated__/graphql';
import {
  OpensBarChart,
  buildChartData,
  buildStatsRange,
} from '@modules/_shared/components/document-stats/OpensBarChart';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { UserAvatar } from '@modules/_shared/components/UserAvatar';
import { useCopiedTooltip } from '@modules/_shared/hooks/useCopiedTooltip';
import { copyPromiseToClipboard } from '@modules/_shared/utils/clipboard';
import { createDocumentUrlForTemplate } from '@modules/_shared/utils/document-url';
import { toHumanReadableDate } from '@modules/_shared/utils/formatting';
import { useLocale } from '@shared/hooks/locale';
import type { Language } from '@shared/LocaleContext';
import { toGlobalId } from '@shared/utils/global-id';

import {
  type BrochureRecipient,
  getRecipientOpenTime,
} from './brochureRecipients';
import {
  GET_BROCHURE_RECIPIENT_STATS,
  TRACKED_BROCHURE_LINK,
} from './brochureRecipientsQueries';

const CHART_RANGE_DAYS = 30;

type RecipientFilter = 'all' | 'opened' | 'not-opened';

const RecipientRow = ({
  recipient,
  documentId,
  templateId,
  language,
  readOnly,
  onError,
  onLinkCopied,
}: {
  recipient: BrochureRecipient;
  documentId: string;
  templateId: string | null;
  language: Language;
  readOnly: boolean;
  onError: (error: ErrorLike) => void;
  onLinkCopied: () => void;
}) => {
  const { t, locale } = useLocale();
  const [expanded, setExpanded] = useState(false);
  const [entered, setEntered] = useState(false);

  const [range, setRange] = useState(() => buildStatsRange(CHART_RANGE_DAYS));
  const opened = recipient.total_opens > 0;

  const [createLink, { loading: minting }] = useMutation(
    TRACKED_BROCHURE_LINK,
    {
      onError,
    },
  );
  const { showCopiedTooltip, onCopied } = useCopiedTooltip();

  const handleCopyLink = useCallback(
    (event: React.MouseEvent) => {
      // Don't let the copy affordance toggle the row's chart.
      event.stopPropagation();
      if (templateId == null) {
        return;
      }
      const url = createDocumentUrlForTemplate(
        templateId,
        documentId,
        language,
      ).href;
      const linkPromise = createLink({
        variables: {
          url,
          documentType: Doc_Template_Type_Enum_Enum.LotBrochure,
          recipientId: toGlobalId('User', recipient.user_id),
        },
      }).then(res => {
        const trackedUrl = res.data?.tracked_document_url?.url;
        if (trackedUrl == null) {
          throw new Error(t('somethingWentWrong'));
        }
        return trackedUrl;
      });

      const showCopyError = (error: unknown) => {
        onError(
          error instanceof Error ? error : new Error(t('somethingWentWrong')),
        );
      };

      copyPromiseToClipboard(linkPromise, {
        onCopied: () => {
          onCopied();
          onLinkCopied();
        },
        onError: showCopyError,
      });
    },
    [
      templateId,
      documentId,
      language,
      recipient.user_id,
      createLink,
      onCopied,
      onError,
      onLinkCopied,
      t,
    ],
  );

  let copyIcon = <Copy size={16} />;
  if (minting) {
    copyIcon = <CircularProgress size={16} />;
  } else if (showCopiedTooltip) {
    copyIcon = <Check size={16} color="var(--mui-palette-success-main)" />;
  }

  const { data, loading, error } = useQuery(GET_BROCHURE_RECIPIENT_STATS, {
    variables: {
      documentId,
      recipientId: toGlobalId('User', recipient.user_id),
      from: range.from,
      to: range.to,
    },
    skip: !opened || !expanded,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (error != null) {
      onError(error);
    }
  }, [error, onError]);

  const lastOpen = useMemo(() => {
    if (recipient.last_open_at == null) {
      return null;
    }
    try {
      return toHumanReadableDate(locale, recipient.last_open_at, {
        month: 'short',
      });
    } catch {
      return null;
    }
  }, [recipient.last_open_at, locale]);

  const stats = data?.document_stats;
  const chartData = buildChartData(
    stats?.daily ?? [],
    CHART_RANGE_DAYS,
    range.to,
  );
  let engagementLabel = t('Not viewed yet');

  if (opened && lastOpen != null) {
    engagementLabel = t('Viewed {{count}} time, last on {{date}}', {
      count: recipient.total_opens,
      date: lastOpen,
    });
  } else if (opened) {
    engagementLabel = t('Viewed {{count}} time', {
      count: recipient.total_opens,
    });
  }

  return (
    <Box>
      <ListItemButton
        disableRipple={!opened}
        onClick={
          opened
            ? () => {
                if (!expanded) {
                  setRange(buildStatsRange(CHART_RANGE_DAYS));
                }
                setExpanded(prev => !prev);
              }
            : undefined
        }
        sx={{
          alignItems: 'center',
          cursor: opened ? 'pointer' : 'default',
          gap: 1.5,
          py: 1.5,
        }}
      >
        <UserAvatar
          nameFallback={recipient.name?.trim() || '?'}
          size={36}
          sx={{ fontSize: 14 }}
        />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
            {recipient.name ?? t('Unnamed buyer')}
          </Typography>
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ alignItems: 'center', color: 'text.secondary' }}
          >
            {opened ? (
              <MailCheck size={14} color="var(--mui-palette-success-main)" />
            ) : (
              <BookOpen size={14} color="var(--mui-palette-text-disabled)" />
            )}
            <Typography variant="caption" color="text.secondary" noWrap>
              {engagementLabel}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ alignItems: 'center', flexShrink: 0 }}
        >
          {templateId != null && !readOnly && (
            <Tooltip
              title={showCopiedTooltip ? t('Copied') : t('Copy link')}
              placement="top"
              arrow
            >
              <IconButton
                size="small"
                onClick={handleCopyLink}
                disabled={minting}
                aria-label={t('Copy link')}
              >
                {copyIcon}
              </IconButton>
            </Tooltip>
          )}
          {opened && (
            <ChevronDown
              size={18}
              style={{
                transform: expanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.15s ease',
                flexShrink: 0,
              }}
            />
          )}
        </Stack>
      </ListItemButton>

      <Collapse
        in={opened && expanded}
        unmountOnExit
        onEntered={() => setEntered(true)}
        onExited={() => setEntered(false)}
      >
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {t('Last {{days}} days', { days: CHART_RANGE_DAYS })}
          </Typography>
          {!entered || (loading && stats == null) ? (
            <Stack
              sx={{
                height: 140,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={20} />
            </Stack>
          ) : (
            <OpensBarChart
              data={chartData}
              totalOpensLabel={t('Views')}
              uniqueOpensLabel={t('People')}
              locale={locale}
              height={140}
              showUniqueSplit={false}
            />
          )}
        </Box>
      </Collapse>
      <Divider />
    </Box>
  );
};

export const BrochureRecipientsDrawer = ({
  recipients,
  documentId,
  templateId,
  language,
  readOnly = false,
  onClose,
}: {
  recipients: BrochureRecipient[];
  documentId: string;
  templateId: string | null;
  language: Language;
  readOnly?: boolean;
  onClose: () => void;
}) => {
  const { t } = useLocale();
  const [filter, setFilter] = useState<RecipientFilter>('all');
  const [currentError, setCurrentError] = useState<ErrorLike | null>(null);
  const { showCopiedTooltip, onCopied, resetCopied } = useCopiedTooltip();

  const openedCount = useMemo(
    () => recipients.filter(recipient => recipient.total_opens > 0).length,
    [recipients],
  );

  const sortedRecipients = useMemo(
    () =>
      recipients
        .map((recipient, index) => ({ recipient, index }))
        .sort((a, b) => {
          const aOpened = a.recipient.total_opens > 0;
          const bOpened = b.recipient.total_opens > 0;

          if (aOpened !== bOpened) {
            return aOpened ? -1 : 1;
          }

          if (aOpened) {
            const timeDiff =
              getRecipientOpenTime(b.recipient) -
              getRecipientOpenTime(a.recipient);
            if (timeDiff !== 0) {
              return timeDiff;
            }
          }

          return a.index - b.index;
        })
        .map(({ recipient }) => recipient),
    [recipients],
  );

  const visibleRecipients = useMemo(
    () =>
      sortedRecipients.filter(recipient => {
        if (filter === 'opened') {
          return recipient.total_opens > 0;
        }
        if (filter === 'not-opened') {
          return recipient.total_opens === 0;
        }
        return true;
      }),
    [filter, sortedRecipients],
  );

  return (
    <Drawer
      onClose={onClose}
      title={t('Views by buyer')}
      maxWidth={440}
      PaperProps={{ sx: { bgcolor: 'background.paper' } }}
    >
      {recipients.length === 0 ? (
        <Stack
          sx={{
            alignItems: 'center',
            color: 'text.secondary',
            gap: 1,
            px: 3,
            py: 4,
            textAlign: 'center',
          }}
        >
          <BookOpen size={28} color="var(--mui-palette-text-secondary)" />
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ fontWeight: 600 }}
          >
            {t('No buyer links yet')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('Create a link for a buyer to see their views here.')}
          </Typography>
        </Stack>
      ) : (
        <>
          <Stack spacing={2} sx={{ px: 2, py: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {t('Last {{days}} days', { days: 365 })}
            </Typography>
            <Typography variant="body2">
              {t('{{opened}} of {{total}} buyers with a link have viewed it.', {
                opened: openedCount,
                total: recipients.length,
              })}
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={filter}
              onChange={(_, value: RecipientFilter | null) => {
                if (value != null) {
                  setFilter(value);
                }
              }}
              sx={{
                alignSelf: 'flex-start',
                flexWrap: 'wrap',
                gap: 1,
                '& .MuiToggleButtonGroup-grouped': {
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  color: 'text.secondary',
                  fontWeight: 600,
                  px: 1.5,
                  py: 0.5,
                  textTransform: 'none',
                  '&.Mui-selected': {
                    bgcolor: 'primary.lighter',
                    borderColor: 'primary.light',
                    color: 'primary.main',
                    '&:hover': { bgcolor: 'primary.lighter' },
                  },
                },
              }}
            >
              <ToggleButton value="all">{t('All')}</ToggleButton>
              <ToggleButton value="opened">{t('Viewed')}</ToggleButton>
              <ToggleButton value="not-opened">{t('Not viewed')}</ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <Divider />
          {visibleRecipients.length === 0 ? (
            <Stack
              sx={{
                alignItems: 'center',
                color: 'text.secondary',
                gap: 1,
                px: 3,
                py: 4,
                textAlign: 'center',
              }}
            >
              <BookOpen size={24} color="var(--mui-palette-text-secondary)" />
              <Typography variant="body2" color="text.secondary">
                {t('No buyers match this filter')}
              </Typography>
            </Stack>
          ) : (
            visibleRecipients.map(recipient => (
              <RecipientRow
                key={recipient.user_id}
                recipient={recipient}
                documentId={documentId}
                templateId={templateId}
                language={language}
                readOnly={readOnly}
                onError={setCurrentError}
                onLinkCopied={onCopied}
              />
            ))
          )}
        </>
      )}
      <MutationErrorSnackbar
        error={currentError}
        onClose={() => setCurrentError(null)}
      />
      {showCopiedTooltip && currentError == null && (
        <RASnackbar
          message={t('Link copied to clipboard')}
          onClose={resetCopied}
          severity="success"
        />
      )}
    </Drawer>
  );
};
